import React, { useEffect, useState } from 'react'
import { useTheme } from '../../../../components/hooks/useTheme';
import useGeneral from '../../../../components/hooks/useGeneral';
import useTenant from '../../../../components/hooks/useTenant';
import Cookies from "js-cookie";
import { GridColDef } from '@mui/x-data-grid';
import { CustomDataGrid } from '../../../../components/common/CustomDataGrid';
import baseURL from '../../../../configs/api';
import moment from 'moment';
import { Button, Dialog } from '@mui/material';
import colorConfigs from '../../../../configs/colorConfigs';

type Props = {
    vehicleId: number;
}

const VehicleStatusHistory = ({ vehicleId }: Props) => {
    const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
    const { themeStyles, theme } = useTheme();
    const { dateFormat }: any = useGeneral();
    const { handleTenantError }: any = useTenant();
    const [paginationModel, setPaginationModel] = useState<any>({
        page: 0,
        pageSize: 10,
    });
    const [loading, setLoading] = useState(true);
    const [tableData, setTableData] = useState<any[]>([]);
    const [totalLength, setTotalLength] = useState<number>(0);
    const [openVehicleStatusHistoryDetailszPopup, setOpenVehicleStatusHistoryDetailszPopup] = useState<boolean>(false);
    const [vehicleStatusHistoryDetails, setVehicleStatusHistoryDetails] = useState<any | null>(null);

    const closeIconFill = theme === "dark" ? "fill-[#E4E7EC]" : "fill-[#1D2939]";

    const initialColumns: GridColDef[] = [
        {
            field: "userFullName",
            headerName: "UserName",
            flex: 1,
            align: "left",
            editable: false,
            sortable: false,
            renderCell: (params) => {
                return (
                    <span
                        style={{ color: "var(--app-link-color)" }}
                        className="underline cursor-pointer"
                        onClick={() => {
                            setVehicleStatusHistoryDetails(params?.row);
                            setOpenVehicleStatusHistoryDetailszPopup(true);
                        }}
                    >
                        {params?.value}
                    </span>
                );
            },
        },
        {
            field: "statusName",
            headerName: "Status",
            flex: 1,
            align: "left",
            editable: false,
            sortable: false,
        },
        {
            field: "changeAt",
            headerName: "Change At",
            flex: 1,
            align: "left",
            editable: false,
            sortable: false,
        }
    ];

    useEffect(() => {
        emptySearchApiCall(paginationModel);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel])

    const emptySearchApiCall = (newPaginationModel: any) => {
        setLoading(true);
        fetch(`${baseURL}vehicleStatusHistories/GetVehicleStatusHistoryReportPaging/${vehicleId},${newPaginationModel.page},${newPaginationModel.pageSize}`, {
            headers: {
                'X-Tenant-ID': tenantId,
            }
        })
            .then((res) => res.json())
            .then((data) => {
                const result = data.vehicleStatusHistories.map((item: any) => {
                    return {
                        ...item,
                        changeAt: moment(item?.changeAt).format(`${dateFormat} hh:mm A`),
                        isWashed: item?.isWashed ? "Yes" : "No",
                        isFuelTankFull: item?.isFuelTankFull ? "Yes" : "No",
                        hasNoDashboardWarnings: item?.hasNoDashboardWarnings ? "Yes" : "No",
                        areTiresInGoodCondition: item?.areTiresInGoodCondition ? "Yes" : "No",
                        areFluidLevelsCorrect: item?.areFluidLevelsCorrect ? "Yes" : "No",
                        isSmellGood: item?.isSmellGood ? "Yes" : "No"
                    };
                });

                setTableData(result);
                setTotalLength(data?.count);
                setLoading(false);
                handleTenantError(null);
            })
            .catch((error) => {
                handleTenantError(error);
                setTableData([]);
                setTotalLength(0);
                setLoading(false);
            });
    }

    return (
        <>
            <div className="w-full px-2 mt-[4rem]">
                <div className="mt-[16px]">
                    <div className="flex items-center justify-between">
                        <span className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Vehicle Status History</span>
                    </div>

                    <div className="">
                        <CustomDataGrid
                            filteredRows={tableData}
                            columns={initialColumns?.map(col => ({
                                ...col,
                                headerClassName: themeStyles.table.headerBg,
                            }))}
                            loading={loading}
                            totalLength={totalLength}
                            paginationModel={paginationModel}
                            setPaginationModel={setPaginationModel}
                            autoRowHeight={true}
                        />
                    </div>
                </div>
            </div>

            <Dialog
                open={openVehicleStatusHistoryDetailszPopup}
                maxWidth="md"
                fullScreen={false}
                sx={{
                    '& .MuiDialog-paper': {
                        maxWidth: '600px',
                        width: '100%',
                        borderRadius: '16px',
                    },
                }}
            >
                <div className={`px-[15px] py-[15px] h-full ${themeStyles.main.tailwindSectionBg}`}>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 320.591 320.591"
                        className={`w-3 cursor-pointer float-right ${closeIconFill}`}
                        onClick={() => {
                            setOpenVehicleStatusHistoryDetailszPopup(false);
                            setVehicleStatusHistoryDetails(null);
                        }}
                    >
                        <path d="M30.391 318.583a30.37 30.37 0 0 1-21.56-7.288c-11.774-11.844-11.774-30.973 0-42.817L266.643 10.665c12.246-11.459 31.462-10.822 42.921 1.424 10.362 11.074 10.966 28.095 1.414 39.875L51.647 311.295a30.366 30.366 0 0 1-21.256 7.288z" />
                        <path d="M287.9 318.583a30.37 30.37 0 0 1-21.257-8.806L8.83 51.963C-2.078 39.225-.595 20.055 12.143 9.146c11.369-9.736 28.136-9.736 39.504 0l259.331 257.813c12.243 11.462 12.876 30.679 1.414 42.922-.456.487-.927.958-1.414 1.414a30.368 30.368 0 0 1-23.078 7.288z" />
                    </svg>
                    <div className={`w-full bg-transparent rounded-lg p-[10px] flex flex-col gap-2.51`}>
                        <div className="w-full">
                            <div className={`w-full`}>
                                <div className={`flex justify-between items-center p-2 !pt-0 border-b ${themeStyles.main.borderColor}`}>
                                    <div className="flex items-center gap-1.5">
                                        <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Vehicle Status History Details</h2>
                                    </div>
                                </div>

                                <div className="mt-2 p-2">
                                    <div className="w-full">
                                        <div>
                                            <div className="flex flex-col gap-y-[0.5rem]">
                                                <div>
                                                    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                                                        <div className="flex flex-col gap-y-[0.125rem]">
                                                            <label className={`text-xs font-bold ${themeStyles.form.tailwindLabelTextColor}`}>UserName</label>
                                                            <span className={`text-xs font-medium ${themeStyles.main.textSubHeadingColor}`}>{vehicleStatusHistoryDetails?.userFullName}</span>
                                                        </div>

                                                        <div className="flex flex-col gap-y-[0.125rem]">
                                                            <label className={`text-xs font-bold ${themeStyles.form.tailwindLabelTextColor}`}>Status</label>
                                                            <span className={`text-xs font-medium ${themeStyles.main.textSubHeadingColor}`}>{vehicleStatusHistoryDetails?.statusName}</span>
                                                        </div>

                                                        <div className="flex flex-col gap-y-[0.125rem]">
                                                            <label className={`text-xs font-bold ${themeStyles.form.tailwindLabelTextColor}`}>Change At</label>
                                                            <span className={`text-xs font-medium ${themeStyles.main.textSubHeadingColor}`}>{vehicleStatusHistoryDetails?.changeAt}</span>
                                                        </div>

                                                        <div className="flex flex-col gap-y-[0.125rem]">
                                                            <label className={`text-xs font-bold ${themeStyles.form.tailwindLabelTextColor}`}>Vehicle Washed</label>
                                                            <span className={`text-xs font-medium ${themeStyles.main.textSubHeadingColor}`}>{vehicleStatusHistoryDetails?.isWashed}</span>
                                                        </div>

                                                        <div className="flex flex-col gap-y-[0.125rem]">
                                                            <label className={`text-xs font-bold ${themeStyles.form.tailwindLabelTextColor}`}>Fuel Tank Full</label>
                                                            <span className={`text-xs font-medium ${themeStyles.main.textSubHeadingColor}`}>{vehicleStatusHistoryDetails?.isFuelTankFull}</span>
                                                        </div>

                                                        <div className="flex flex-col gap-y-[0.125rem]">
                                                            <label className={`text-xs font-bold ${themeStyles.form.tailwindLabelTextColor}`}>No Dashboard Warnings</label>
                                                            <span className={`text-xs font-medium ${themeStyles.main.textSubHeadingColor}`}>{vehicleStatusHistoryDetails?.hasNoDashboardWarnings}</span>
                                                        </div>

                                                        <div className="flex flex-col gap-y-[0.125rem]">
                                                            <label className={`text-xs font-bold ${themeStyles.form.tailwindLabelTextColor}`}>Tires in Good Condition</label>
                                                            <span className={`text-xs font-medium ${themeStyles.main.textSubHeadingColor}`}>{vehicleStatusHistoryDetails?.areTiresInGoodCondition}</span>
                                                        </div>

                                                        <div className="flex flex-col gap-y-[0.125rem]">
                                                            <label className={`text-xs font-bold ${themeStyles.form.tailwindLabelTextColor}`}>Fluid Levels Correct (Coolant, Windshield Fluid, DEF)</label>
                                                            <span className={`text-xs font-medium ${themeStyles.main.textSubHeadingColor}`}>{vehicleStatusHistoryDetails?.areFluidLevelsCorrect}</span>
                                                        </div>

                                                        <div className="flex flex-col gap-y-[0.125rem]">
                                                            <label className={`text-xs font-bold ${themeStyles.form.tailwindLabelTextColor}`}>Smells Good</label>
                                                            <span className={`text-xs font-medium ${themeStyles.main.textSubHeadingColor}`}>{vehicleStatusHistoryDetails?.isSmellGood}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-4 mt-6">
                                                <Button
                                                    type="button"
                                                    variant="contained"
                                                    sx={{
                                                        backgroundColor: colorConfigs.btn.cancel.bg,
                                                        border: colorConfigs.btn.cancel.border,
                                                        color: colorConfigs.btn.cancel.color
                                                    }}
                                                    onClick={() => {
                                                        setOpenVehicleStatusHistoryDetailszPopup(false);
                                                        setVehicleStatusHistoryDetails(null);
                                                    }}
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
        </>
    )
}

export default VehicleStatusHistory
